import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Loader2, ArrowLeft, Eye, EyeOff, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  // Memoized input change handler
  const handleInputChange = useCallback((field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value
      }));
    }, []
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  // Password validation rules
  const passwordValidation = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 6,
      hasNumber: /\d/.test(password),
      hasLetter: /[a-zA-Z]/.test(password),
      noSpaces: !/\s/.test(password)
    };
  }, [formData.password]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordValidation).every(Boolean) && formData.password.length > 0;
  }, [passwordValidation, formData.password]);

  const passwordsMatch = useMemo(() => {
    return formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;
  }, [formData.password, formData.confirmPassword]);

  const validateForm = useCallback(() => {
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      toast.error('Password is required');
      return false;
    }

    if (!isPasswordValid) {
      toast.error('Password does not meet requirements');
      return false;
    }

    if (!formData.confirmPassword) {
      toast.error('Please confirm your password');
      return false;
    }

    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return false;
    }

    return true;
  }, [formData, isPasswordValid, passwordsMatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await signUp(formData.email.trim(), formData.password);
      toast.success('Account created successfully!');
      navigate('/login');
    } catch (error: any) {
      console.error('Signup error:', error);

      // Handle different types of errors
      let errorMessage = 'Failed to create account. Please try again.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status === 409) {
        errorMessage = 'An account with this email already exists';
      } else if (error?.response?.status === 429) {
        errorMessage = 'Too many signup attempts. Please try again later.';
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network.';
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const ValidationIndicator: React.FC<{ isValid: boolean; text: string }> = ({ isValid, text }) => (
    <div className={`flex items-center space-x-2 text-xs transition-colors duration-200 ${isValid ? 'text-green-600' : 'text-gray-400'
      }`}>
      {isValid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link
            to="/"
            className="flex items-center justify-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="bg-primary-500 p-2 rounded-lg shadow-md">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">First Ping</h1>
          </div>

          <h2 className="text-center text-2xl font-semibold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
            >
              Sign in here
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    disabled={loading}
                    className={`appearance-none block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200 ${loading
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                    placeholder="Enter your email"
                  />
                  <Mail className={`h-5 w-5 absolute left-3 top-2.5 transition-colors duration-200 ${loading ? 'text-gray-300' : 'text-gray-400'
                    }`} />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    disabled={loading}
                    className={`appearance-none block w-full px-3 py-2 pl-10 pr-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200 ${loading
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                    placeholder="Create a password"
                  />
                  <Lock className={`h-5 w-5 absolute left-3 top-2.5 transition-colors duration-200 ${loading ? 'text-gray-300' : 'text-gray-400'
                    }`} />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                    className={`absolute right-3 top-2.5 transition-colors duration-200 ${loading
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-gray-600'
                      }`}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Password Requirements */}
                {formData.password && (
                  <div className="mt-2 space-y-1 p-3 bg-gray-50 rounded-md">
                    <ValidationIndicator isValid={passwordValidation.minLength} text="At least 6 characters" />
                    <ValidationIndicator isValid={passwordValidation.hasLetter} text="Contains letters" />
                    <ValidationIndicator isValid={passwordValidation.hasNumber} text="Contains numbers" />
                    <ValidationIndicator isValid={passwordValidation.noSpaces} text="No spaces" />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    disabled={loading}
                    className={`appearance-none block w-full px-3 py-2 pl-10 pr-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200 ${loading
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : formData.confirmPassword && !passwordsMatch
                          ? 'border-red-300 hover:border-red-400'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    placeholder="Confirm your password"
                  />
                  <Lock className={`h-5 w-5 absolute left-3 top-2.5 transition-colors duration-200 ${loading ? 'text-gray-300' : 'text-gray-400'
                    }`} />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    disabled={loading}
                    className={`absolute right-3 top-2.5 transition-colors duration-200 ${loading
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-gray-600'
                      }`}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {formData.confirmPassword && (
                  <div className="mt-1">
                    <ValidationIndicator isValid={passwordsMatch} text="Passwords match" />
                  </div>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || !isPasswordValid || !passwordsMatch}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 disabled:hover:bg-primary-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating account...
                    </>
                  ) : (
                    'Create account'
                  )}
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  🎉 Get <span className="font-semibold text-primary-600">5 free credits</span> when you sign up!
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;